/**
 * MentionNotificationPanel - Display mention notifications
 *
 * Two piles, not one flat list:
 * - "New" — unread. This is what the badge counts.
 * - "Earlier" — read but not yet cleared, collapsed by default. Opening a
 *   mention cascades every tag on that entity into here, so a thing you've
 *   already looked at stops shouting at you without being deleted.
 *
 * Clicking a row IS reading it — no separate "Mark read" press required.
 *
 * V107.52.24 — Migrated from hardcoded dark-mode classes to semantic theme classes
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mention, MentionEntityType, MentionStatus } from '@/types/mention.types';
import {
  Sparkles,
  FolderKanban,
  AlertCircle,
  MessageSquare,
  Check,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  CheckCircle2,
  Briefcase,
  Headphones,
  Users,
  X,
} from 'lucide-react';
import { formatRelativeTime } from '@/utils/date-utils';
import { buildMentionNavPath } from '@/services/mention.service';

interface MentionNotificationPanelProps {
  mentions: Mention[];
  unreadCount: number;
  onMarkAsRead: (mentionId: string) => void;
  onMarkAllAsRead: () => void;
  /** Cascade — called when a row is opened. Reads every tag on that entity. */
  onOpenMention?: (mention: Mention) => void;
  onClear?: (mentionId: string) => void;
  onClearAll?: () => void;
  /** Sweep only the "Earlier" pile. */
  onClearRead?: () => void;
  onClose?: () => void;
}

export function MentionNotificationPanel({
  mentions,
  unreadCount,
  onMarkAsRead,
  onMarkAllAsRead,
  onOpenMention,
  onClear,
  onClearAll,
  onClearRead,
  onClose,
}: MentionNotificationPanelProps) {
  const navigate = useNavigate();
  const [showEarlier, setShowEarlier] = useState(false);

  const newMentions = mentions.filter((m) => m.status === MentionStatus.UNREAD);
  const earlierMentions = mentions.filter((m) => m.status !== MentionStatus.UNREAD);

  const handleMentionClick = (mention: Mention) => {
    // Reading happens on open, whether or not there's somewhere to navigate
    // to — a tag you've opened is a tag you've seen.
    onOpenMention?.(mention);
    const path = buildMentionNavPath(mention);
    if (path) {
      navigate(path);
      onClose?.();
    }
  };

  const getEntityIcon = (entityType: MentionEntityType) => {
    switch (entityType) {
      case MentionEntityType.DEAL:
        return <Sparkles className="w-3.5 h-3.5 text-brand-secondary" />;
      case MentionEntityType.PROJECT:
        return <FolderKanban className="w-3.5 h-3.5 text-blue-400" />;
      case MentionEntityType.FAULT:
        return <AlertCircle className="w-3.5 h-3.5 text-amber-400" />;
      case MentionEntityType.JOB:
        return <Briefcase className="w-3.5 h-3.5 text-sky-400" />;
      case MentionEntityType.BUG_FIX:
        return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />;
      case MentionEntityType.SERVICE_REQUEST:
        return <Headphones className="w-3.5 h-3.5 text-cyan-400" />;
      case MentionEntityType.MEETING:
        return <Users className="w-3.5 h-3.5 text-violet-400" />;
      default:
        return <MessageSquare className="w-3.5 h-3.5 text-theme-muted" />;
    }
  };

  const getEntityLabel = (entityType: MentionEntityType) => {
    switch (entityType) {
      case MentionEntityType.DEAL:
        return 'Deal';
      case MentionEntityType.PROJECT:
        return 'Project';
      case MentionEntityType.FAULT:
        return 'Fault';
      case MentionEntityType.JOB:
        return 'Job';
      case MentionEntityType.BUG_FIX:
        return 'Report';
      case MentionEntityType.SERVICE_REQUEST:
        return 'Service Request';
      case MentionEntityType.MEETING:
        return 'Meeting';
      default:
        return 'Note';
    }
  };

  if (mentions.length === 0) {
    return (
      <div className="p-6 text-center">
        <MessageSquare className="w-8 h-8 text-theme-muted mx-auto mb-2" />
        <p className="text-sm text-theme-tertiary">No mentions yet</p>
        <p className="text-xs text-theme-muted mt-1">
          You'll be notified when someone @mentions you
        </p>
      </div>
    );
  }

  return (
    <div className="max-h-[480px] overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-surface-card backdrop-blur-xl border-b border-theme-soft px-4 py-3 z-10">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <MessageSquare className="w-4 h-4 text-brand-secondary" />
            <h3 className="text-sm font-semibold text-theme-primary">Mentions</h3>
            {unreadCount > 0 && (
              <span className="px-1.5 py-0.5 bg-brand-secondary/20 text-brand-secondary text-[10px] font-bold rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {unreadCount > 0 && (
              <button
                onClick={onMarkAllAsRead}
                className="flex items-center gap-1 px-2 py-1 text-[10px] text-brand-secondary hover:text-brand-secondary/80 transition-colors"
              >
                <Check className="w-3 h-3" />
                Mark all read
              </button>
            )}
            {onClearAll && mentions.length > 1 && (
              <button
                onClick={onClearAll}
                className="flex items-center gap-1 px-2 py-1 text-[10px] text-theme-muted hover:text-theme-secondary transition-colors"
                title="Clear all mentions"
              >
                <X className="w-3 h-3" />
                Clear all
              </button>
            )}
          </div>
        </div>
      </div>

      {/* New — unread. What the badge counts. */}
      <div className="divide-y divide-theme-soft">
        {newMentions.map((mention) => (
          <MentionRow
            key={mention.id}
            mention={mention}
            icon={getEntityIcon(mention.entityType)}
            entityLabel={getEntityLabel(mention.entityType)}
            onClick={() => handleMentionClick(mention)}
            onMarkAsRead={onMarkAsRead}
            onClear={onClear}
          />
        ))}
      </div>

      {newMentions.length === 0 && earlierMentions.length > 0 && (
        <div className="px-4 py-6 text-center">
          <Check className="w-6 h-6 text-emerald-400/70 mx-auto mb-1.5" />
          <p className="text-xs text-theme-tertiary">You're all caught up</p>
          <p className="text-[10px] text-theme-muted mt-0.5">
            Everything you've read is under Earlier
          </p>
        </div>
      )}

      {/* Earlier — already read, collapsed. The cascade's landing pad. */}
      {earlierMentions.length > 0 && (
        <div className="border-t border-theme-soft">
          <div className="flex items-center gap-1 px-3 py-2">
            <button
              type="button"
              onClick={() => setShowEarlier((v) => !v)}
              className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-theme-muted hover:text-theme-secondary transition-colors"
              aria-expanded={showEarlier}
            >
              {showEarlier ? (
                <ChevronDown className="w-3 h-3" />
              ) : (
                <ChevronRight className="w-3 h-3" />
              )}
              Earlier
              <span className="text-theme-muted/70">{earlierMentions.length}</span>
            </button>
            {onClearRead && (
              <button
                onClick={onClearRead}
                className="ml-auto flex items-center gap-1 px-2 py-1 text-[10px] text-theme-muted hover:text-theme-secondary transition-colors"
                title="Clear everything you've already read"
              >
                <X className="w-3 h-3" />
                Clear read
              </button>
            )}
          </div>

          {showEarlier && (
            <div className="divide-y divide-theme-soft">
              {earlierMentions.map((mention) => (
                <MentionRow
                  key={mention.id}
                  mention={mention}
                  icon={getEntityIcon(mention.entityType)}
                  entityLabel={getEntityLabel(mention.entityType)}
                  onClick={() => handleMentionClick(mention)}
                  onMarkAsRead={onMarkAsRead}
                  onClear={onClear}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MentionRow({
  mention,
  icon,
  entityLabel,
  onClick,
  onMarkAsRead,
  onClear,
}: {
  mention: Mention;
  icon: React.ReactNode;
  entityLabel: string;
  onClick: () => void;
  onMarkAsRead: (mentionId: string) => void;
  onClear?: (mentionId: string) => void;
}) {
  const unread = mention.status === MentionStatus.UNREAD;

  return (
    <div
      className={`
        relative group px-4 py-3 hover:bg-surface-hover transition-colors cursor-pointer
        ${unread ? 'bg-brand-secondary/5' : 'opacity-75 hover:opacity-100'}
      `}
      onClick={onClick}
    >
      <div className="flex gap-3">
        {/* Entity Icon */}
        <div className="shrink-0 mt-0.5">{icon}</div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-theme-primary font-medium truncate">
                {mention.entityTitle}
              </p>
              <p className="text-[10px] text-theme-muted">
                <span className="font-medium text-brand-secondary">{mention.createdByName}</span>{' '}
                {mention.entityType === MentionEntityType.BUG_FIX
                  ? 'resolved your report'
                  : 'mentioned you in a note'}
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <span className="text-[9px] text-theme-muted">
                {formatRelativeTime(mention.createdAt)}
              </span>
              {onClear && mention.id && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (mention.id) onClear(mention.id);
                  }}
                  className="p-0.5 rounded text-theme-muted/60 hover:text-theme-secondary hover:bg-surface-hover transition-colors"
                  title="Clear mention"
                  aria-label={`Clear mention from ${mention.createdByName}`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          {/* Note Excerpt */}
          <div className="bg-surface-hover border border-theme-soft rounded-lg px-2.5 py-2 mb-2">
            <p className="text-xs text-theme-secondary line-clamp-2">{mention.noteExcerpt}</p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {unread && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (mention.id) onMarkAsRead(mention.id);
                }}
                className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-brand-secondary hover:bg-brand-secondary/10 transition-colors"
              >
                <Check className="w-3 h-3" />
                Mark read
              </button>
            )}
            {mention.linkPath && (
              <button className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-theme-muted hover:text-theme-secondary hover:bg-surface-hover transition-colors group-hover:text-brand-secondary">
                <ExternalLink className="w-3 h-3" />
                View {entityLabel.toLowerCase()}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Unread indicator */}
      {unread && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-brand-secondary rounded-l-xl"></div>
      )}
    </div>
  );
}
